import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useEffect, useState, useContext, useRef } from 'react';
import { mutate } from 'swr';
import useSWR from 'swr';
import yogBg from '../images/yog-bg.jpg';
import { FaLock, FaEnvelope, FaLeaf, FaSpa, FaEye, FaEyeSlash, FaPhone } from 'react-icons/fa';
import HttpInterceptor from '../lib/HttpInterceptor'; // your HTTP lib
import { toast } from "react-toastify";
import UserContext from '../Components/UserContext';
import Fetcher from '../lib/Fetcher';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { setSession } = useContext(UserContext);
  const trainerId = searchParams.get('trainerId');
  // Get all programId values (can be multiple)
  const programIds = searchParams.getAll('programId');
  const [showPassword, setShowPassword] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const formikRef = useRef(null);

  // Detect PWA standalone mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    window.addEventListener('resize', checkPWA);
    return () => window.removeEventListener('resize', checkPWA);
  }, []);

  // Check if user is already logged in
  // Configure SWR to not retry on 401 errors (unauthorized is a valid state, not an error to retry)
  const { data: session, error: sessionError, isLoading: isSessionLoading } = useSWR('/auth/session', Fetcher, {
    shouldRetryOnError: (error) => {
      // Don't retry on 401 (Unauthorized) - user is simply not logged in
      if (error?.response?.status === 401) {
        return false;
      }
      // Retry on other errors (network issues, etc.)
      return true;
    },
    errorRetryCount: 0, // Don't retry 401 errors
    revalidateOnFocus: false, // Don't revalidate when window gains focus
    revalidateOnReconnect: true, // Revalidate when network reconnects
  });

  // Clear form state if user just logged out (prevents password from showing on back button)
  // Also clear form on every mount to prevent cached passwords from appearing
  useEffect(() => {
    const justLoggedOut = sessionStorage.getItem('just-logged-out');

    // Always clear password fields on mount to prevent autofill/cached passwords
    const clearPasswordFields = () => {
      // Clear any autofilled password fields immediately
      const passwordInput = document.getElementById('password');
      if (passwordInput) {
        passwordInput.value = '';
        passwordInput.type = 'password'; // Ensure it's hidden
        // Force clear any browser autofill
        passwordInput.setAttribute('autocomplete', 'off');
      }
      const identifierInput = document.getElementById('identifier');
      if (identifierInput) {
        identifierInput.value = '';
      }
    };

    // Clear immediately on mount
    clearPasswordFields();

    // Clear again after a short delay to catch any delayed autofill
    const timeoutId = setTimeout(clearPasswordFields, 100);

    if (justLoggedOut === 'true') {
      // Clear the flag
      sessionStorage.removeItem('just-logged-out');
      // Clear form if it exists
      if (formikRef.current) {
        formikRef.current.resetForm();
      }
      // Clear password fields again
      clearPasswordFields();
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Prevent back navigation to login page after logout
  // Also ensure password is cleared if user navigates back via browser history
  useEffect(() => {
    const handlePopState = () => {
      // Clear password fields when navigating back to login
      const passwordInput = document.getElementById('password');
      if (passwordInput) {
        passwordInput.value = '';
        passwordInput.type = 'password';
      }
      const identifierInput = document.getElementById('identifier');
      if (identifierInput) {
        identifierInput.value = '';
      }
      // Reset form if it exists
      if (formikRef.current) {
        formikRef.current.resetForm();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle OAuth redirect messages with sanitization
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const messageParam = searchParams.get('message');

    // Sanitize and validate error parameter
    if (errorParam) {
      const sanitizedError = errorParam.replace(/[^a-zA-Z0-9\-_]/g, '');
      if (sanitizedError === 'oauth_invalid_client' && sanitizedError === errorParam) {
        toast.error('Google OAuth is not properly configured. Please contact the administrator.');
      } else if (sanitizedError === 'oauth_failed' && sanitizedError === errorParam) {
        // Use custom message if provided, otherwise default message
        if (messageParam) {
          // Sanitize message but allow more characters for better UX
          const sanitizedMessage = messageParam.replace(/[<>\"']/g, '');
          toast.error(sanitizedMessage || 'Google authentication failed. Please try again.');
        } else {
          toast.error('Google authentication failed. Please try again.');
        }
      } else if (sanitizedError === 'oauth_account_exists' && sanitizedError === errorParam) {
        // Account exists error from OAuth
        if (messageParam) {
          const sanitizedMessage = messageParam.replace(/[<>\"']/g, '');
          toast.warning(sanitizedMessage || 'An account with this email already exists. Please sign in with your password.');
        } else {
          toast.warning('An account with this email already exists. Please sign in with your password or use the "Forgot Password" option.');
        }
      } else if (sanitizedError === 'oauth_validation' && sanitizedError === errorParam) {
        // Validation error from OAuth
        if (messageParam) {
          const sanitizedMessage = messageParam.replace(/[<>\"']/g, '');
          toast.error(sanitizedMessage || 'Unable to create account. Please try again or contact support.');
        } else {
          toast.error('Unable to create account. Please try again or contact support.');
        }
      }
    }

    // Sanitize and validate message parameter (for non-OAuth messages)
    if (messageParam && !errorParam?.includes('oauth')) {
      const sanitizedMessage = messageParam.replace(/[^a-zA-Z0-9\-_]/g, '');
      if (sanitizedMessage === 'pending_approval' && sanitizedMessage === messageParam) {
        toast.info('Your account is pending admin approval. You will be notified via email once approved.');
      }
    }
  }, [searchParams]);

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (session && session.role && !isSessionLoading) {
      // Check for generic redirect param first
      const redirectPath = searchParams.get('redirect');
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
        return;
      }

      // Check if we have trainerId and programId parameters (from Services page)
      const params = new URLSearchParams();
      const hasTrainerId = trainerId && trainerId.trim().length > 0;
      const hasProgramIds = programIds && programIds.length > 0;

      if (hasTrainerId || hasProgramIds) {
        // User came from Services page, redirect back with parameters
        if (hasTrainerId) {
          const sanitizedTrainerId = trainerId.replace(/[^a-zA-Z0-9]/g, '');
          if (sanitizedTrainerId) {
            params.set('trainerId', sanitizedTrainerId);
          }
        }
        if (hasProgramIds) {
          programIds.forEach((id) => {
            const sanitizedProgramId = id.replace(/[^a-zA-Z0-9]/g, '');
            if (sanitizedProgramId) {
              params.append('programId', sanitizedProgramId);
            }
          });
        }

        // Only redirect to trainer profile if user is a student (not admin or trainer)
        if (session.role !== 'admin' && session.role !== 'super_admin' && !(session.isTrainer && session.trainerStatus === 'active')) {
          const sanitizedTrainerId = trainerId.replace(/[^a-zA-Z0-9]/g, '');
          navigate(`/student/trainer/${sanitizedTrainerId}?${params.toString()}`, { replace: true });
          return;
        }
      }

      // Default redirects for users without trainerId/programId or admin/trainer users
      if (session.role === 'admin' || session.role === 'super_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (session.isTrainer && session.trainerStatus === 'active') {
        navigate('/trainer/home', { replace: true });
      } else {
        navigate('/student/home', { replace: true });
      }
    }
  }, [session, isSessionLoading, navigate, trainerId, programIds]);

  const validationSchema = Yup.object({
    identifier: Yup.string()
      .test('is-email-or-mobile', 'Invalid email or mobile number', function (value) {
        if (!value) return false;
        // Check if it's an email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(value)) return true;
        // Check if it's a valid mobile (10 digits)
        const mobileRegex = /^[6-9]\d{9}$/;
        const cleanMobile = value.replace(/[^\d]/g, '');
        return mobileRegex.test(cleanMobile);
      })
      .required('Email or mobile number is required'),
    password: Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .max(12, 'Password must be at most 12 characters')
      .required('Password is required')
  });

  // Show loading state while checking session
  if (isSessionLoading) {
    return (
      <section className="min-h-screen flex items-center justify-center p-4 relative">
        <div className="absolute inset-0 -z-10">
          <img
            src={yogBg}
            alt="Yoga background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
        </div>
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Checking session...</p>
        </div>
      </section>
    );
  }

  // If already logged in, the useEffect will handle redirect
  // But show loading while redirect happens
  if (session && session.role) {
    return (
      <section className="min-h-screen flex items-center justify-center p-4 relative">
        <div className="absolute inset-0 -z-10">
          <img
            src={yogBg}
            alt="Yoga background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
        </div>
        <div className="relative z-10 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Redirecting to dashboard...</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`min-h-screen flex items-center justify-center p-4 relative ${isPWA ? 'bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50' : ''}`}>
      {/* Background */}
      {!isPWA && (
        <div className="absolute inset-0 -z-10">
          <img
            src={yogBg}
            alt="Yoga background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-emerald-900/80"></div>
        </div>
      )}

      {/* Login Card */}
      <div className={`relative z-10 w-full ${isPWA ? 'max-w-lg' : 'max-w-md'} my-8`}>
        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 border-2 border-teal-200/60 shadow-2xl' : 'bg-white shadow-2xl border border-white/20'} rounded-xl sm:rounded-2xl overflow-hidden relative`}>
          {/* PWA Decorative Elements */}
          {isPWA && (
            <>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-100/30 to-blue-100/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100/30 to-indigo-100/20 rounded-full blur-3xl"></div>
            </>
          )}

          {/* Header */}
          <div className={`bg-gradient-to-r from-teal-600 to-emerald-600 p-6 sm:p-8 text-center relative z-10`}>
            <div className={`${isPWA ? 'w-24 h-24 bg-white/20 backdrop-blur-sm ring-4 ring-white/30' : 'w-20 h-20 bg-white/10'} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
              <FaLeaf className={`${isPWA ? 'text-4xl' : 'text-3xl'} text-white transform rotate-45`} />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="relative">
                <div className="absolute left-0 w-1 h-5 bg-white/40 rounded-full blur-sm"></div>
                <div className="relative w-1 h-5 bg-white rounded-full"></div>
              </div>
              <h2 className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-2xl md:text-3xl'} font-bold text-white drop-shadow-lg`}>
                Welcome to Yogvital
              </h2>
              <div className="relative">
                <div className="absolute left-0 w-1 h-5 bg-white/40 rounded-full blur-sm"></div>
                <div className="relative w-1 h-5 bg-white rounded-full"></div>
              </div>
            </div>
            <p className={`${isPWA ? 'text-base sm:text-lg' : 'text-sm'} text-white/90 font-medium`}>
              Sign in to continue your wellness journey
            </p>
          </div>

          {/* Form */}
          <Formik
            innerRef={formikRef}
            initialValues={{ identifier: '', password: '' }}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={async (values, { setSubmitting, setErrors }) => {
              try {
                // Detect if input is email or mobile
                const identifier = values.identifier.trim();
                const isEmail = identifier.includes('@');

                // Prepare request body based on input type
                const requestBody = isEmail
                  ? {
                    email: identifier.toLowerCase().substring(0, 255),
                    password: values.password
                  }
                  : {
                    mobile: identifier.replace(/[^\d]/g, ''), // Remove non-digits
                    password: values.password
                  };

                const { data } = await HttpInterceptor.post('/auth/login', requestBody);
                toast.success('Login successful!');

                // CRITICAL: Clear SWR cache FIRST to remove any stale session data
                // This is especially important after logging out from admin dashboard
                mutate('/auth/session', undefined, { revalidate: false });

                // Clear the UserContext session to ensure no stale data
                setSession(null);

                // Wait for cookie to be set by browser and ensure cache is cleared
                await new Promise(resolve => setTimeout(resolve, 300));

                // Fetch fresh session to determine redirect path
                try {
                  // Use a timestamp query param to bypass any HTTP cache
                  const timestamp = Date.now();
                  const sessionResponse = await HttpInterceptor.get(`/auth/session?t=${timestamp}`);
                  const session = sessionResponse.data;

                  // Debug logging to verify session data
                  console.log('Login - Fresh session data:', {
                    role: session.role,
                    isTrainer: session.isTrainer,
                    trainerStatus: session.trainerStatus,
                    trainerId: session.trainerId
                  });

                  // CRITICAL: Update UserContext and SWR cache with fresh session data
                  // This ensures Guard component and other components have correct session
                  setSession(session);
                  // Update SWR cache and trigger revalidation so Guard component picks up the change
                  // Using true for revalidate ensures all SWR hooks using this key get the update
                  mutate('/auth/session', session, true);

                  // Wait longer to ensure Guard component's SWR has processed the update
                  await new Promise(resolve => setTimeout(resolve, 300));

                  // Redirect based on role
                  // Use window.location.href for full page reload to ensure clean state
                  // This prevents race conditions with Guard component's SWR fetching

                  // Check for generic redirect param first
                  const redirectPath = searchParams.get('redirect');
                  if (redirectPath) {
                    window.location.href = redirectPath;
                    return;
                  }

                  if (session.role === 'admin' || session.role === 'super_admin') {
                    window.location.href = '/admin/dashboard';
                  } else if (session.isTrainer && session.trainerStatus === 'active') {
                    // Approved trainers go to trainer dashboard by default
                    console.log('Login - Redirecting to trainer dashboard', {
                      isTrainer: session.isTrainer,
                      trainerStatus: session.trainerStatus,
                      trainerId: session.trainerId
                    });
                    window.location.href = '/trainer/home';
                  } else {
                    console.log('Login - Redirecting to member dashboard', {
                      isTrainer: session.isTrainer,
                      trainerStatus: session.trainerStatus,
                      role: session.role
                    });
                    // All other members go to member dashboard
                    // If trainerId is present, redirect to courses page with that trainer and programs
                    if (trainerId) {
                      // Sanitize trainerId and programIds before using in URL
                      const sanitizedTrainerId = trainerId.replace(/[^a-zA-Z0-9]/g, '');
                      const sanitizedProgramIds = programIds
                        .map(id => id.replace(/[^a-zA-Z0-9]/g, ''))
                        .filter(id => id.length > 0);
                      if (sanitizedTrainerId) {
                        const params = new URLSearchParams();
                        params.set('trainerId', sanitizedTrainerId);
                        sanitizedProgramIds.forEach(id => {
                          params.append('programId', id);
                        });
                        window.location.href = `/student/trainer/${sanitizedTrainerId}?${params.toString()}`;
                      } else {
                        window.location.href = '/student/home';
                      }
                    } else {
                      window.location.href = '/student/home';
                    }
                  }
                } catch (sessionError) {
                  // If session fetch fails, default to member dashboard
                  console.error('Error fetching session:', sessionError);
                  // Still check for trainerId even on error
                  if (trainerId) {
                    const sanitizedTrainerId = trainerId.replace(/[^a-zA-Z0-9]/g, '');
                    const sanitizedProgramIds = programIds
                      .map(id => id.replace(/[^a-zA-Z0-9]/g, ''))
                      .filter(id => id.length > 0);
                    if (sanitizedTrainerId) {
                      const params = new URLSearchParams();
                      params.set('trainerId', sanitizedTrainerId);
                      sanitizedProgramIds.forEach(id => {
                        params.append('programId', id);
                      });
                      window.location.href = `/student/trainer/${sanitizedTrainerId}?${params.toString()}`;
                    } else {
                      window.location.href = '/student/home';
                    }
                  } else {
                    window.location.href = '/student/home';
                  }
                }
              } catch (err) {
                const msg = err.response?.data?.message || 'Login failed. Try again.';

                // Check if this is an OAuth-related error message
                // If user has googleId but no password, they should use OAuth
                if (msg.includes('Google sign-in') || msg.includes('Google button')) {
                  setErrors({ submit: msg });
                  toast.warning(msg);
                } else if (msg.includes('Account not found')) {
                  // Check if user might be trying to login with OAuth account using password
                  // Show a more helpful message
                  const improvedMsg = 'Account not found. If you signed up with Google, please use the "Sign in with Google" button instead, or use "Forgot Password" to create a password.';
                  setErrors({ submit: improvedMsg });
                  toast.warning(improvedMsg);
                } else {
                  setErrors({ submit: msg });
                  toast.error(msg);
                }
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <Form className={`${isPWA ? 'p-6 sm:p-8' : 'p-6'} space-y-5 sm:space-y-6 relative z-10`}>
                <ErrorMessage
                  name="submit"
                  component="div"
                  className={`${isPWA ? 'bg-red-50 border-2 border-red-200 text-red-700' : 'text-red-600'} text-sm text-center p-3 rounded-lg`}
                  role="alert"
                  aria-live="assertive"
                  id="submit-error"
                />

                {/* Email or Mobile */}
                <div className="space-y-2">
                  <label htmlFor="identifier" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium text-gray-700'} block`}>
                    Email or Mobile Number
                    <span className="text-red-500 ml-1" aria-label="required">*</span>
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                      <FaEnvelope className={`${isPWA ? 'text-teal-500' : 'text-gray-400'} h-5 w-5`} />
                    </div>
                    <Field
                      name="identifier"
                      id="identifier"
                      type="text"
                      placeholder="your@email.com or 9876543210"
                      className={`${isPWA ? 'pl-12 pr-4 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                      aria-required="true"
                      aria-describedby="identifier-error"
                      autoComplete="username"
                    />
                  </div>
                  <ErrorMessage
                    name="identifier"
                    component="p"
                    id="identifier-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                    aria-live="polite"
                  />
                </div>

                {/* Hidden dummy password field to prevent browser autofill on real password field */}
                <input
                  type="password"
                  name="dummy-password"
                  autoComplete="new-password"
                  style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
                  tabIndex={-1}
                  aria-hidden="true"
                />
                {/* Password */}
                <div className="space-y-2">
                  <label htmlFor="password" className={`${isPWA ? 'text-sm sm:text-base font-semibold text-teal-800' : 'text-sm font-medium text-gray-700'} block`}>
                    Password
                    <span className="text-red-500 ml-1" aria-label="required">*</span>
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 left-0 ${isPWA ? 'pl-4' : 'pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                      <FaLock className={`${isPWA ? 'text-teal-500' : 'text-gray-400'} h-5 w-5`} />
                    </div>
                    <Field
                      name="password"
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className={`${isPWA ? 'pl-12 pr-12 py-3.5 border-2 border-teal-200/60 bg-white/50 focus:bg-white focus:border-teal-400 focus:ring-2 focus:ring-teal-300/50' : 'pl-10 pr-10 border py-3.5'} w-full rounded-xl sm:rounded-2xl transition-all duration-300`}
                      aria-required="true"
                      aria-describedby="password-error"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute inset-y-0 right-0 ${isPWA ? 'pr-4' : 'pr-3'} flex items-center ${isPWA ? 'text-teal-500 hover:text-teal-600' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                    </button>
                  </div>
                  <ErrorMessage
                    name="password"
                    component="p"
                    id="password-error"
                    className="text-red-500 text-xs mt-1"
                    role="alert"
                    aria-live="polite"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  aria-label={isSubmitting ? 'Signing in, please wait' : 'Sign in to your account'}
                  className={`w-full ${isPWA ? 'bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 hover:from-teal-600 hover:via-blue-600 hover:to-indigo-600 py-3.5 px-6 rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl' : 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 py-3 px-4 rounded-lg shadow-md hover:shadow-lg'} text-white font-bold transition-all duration-300 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'
                    }`}
                >
                  <FaSpa className="text-white" aria-hidden="true" />
                  <span>{isSubmitting ? 'Signing In...' : 'Sign In'}</span>
                </button>

                {/* Google OAuth Button */}
                <div className="relative" role="separator" aria-label="Authentication options">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className={`w-full border-t ${isPWA ? 'border-teal-200/50' : 'border-gray-300'}`}></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className={`px-2 ${isPWA ? 'bg-gradient-to-br from-white to-teal-50/30 text-teal-600 font-medium' : 'bg-white text-gray-500'}`}>
                      Or continue with
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const apiUrl = import.meta.env.VITE_SERVER?.replace(/\/+$/, '');
                    if (!apiUrl) {
                      toast.error('Server URL not configured. Please set VITE_SERVER environment variable.');
                      return;
                    }
                    // Preserve trainerId and programIds and generic redirect in OAuth redirect
                    const params = new URLSearchParams();
                    const redirect = searchParams.get('redirect');
                    if (redirect) {
                      params.set('redirect', redirect);
                    }
                    if (trainerId) {
                      const sanitizedTrainerId = trainerId.replace(/[^a-zA-Z0-9]/g, '');
                      if (sanitizedTrainerId) {
                        params.set('trainerId', sanitizedTrainerId);
                      }
                    }
                    if (programIds && programIds.length > 0) {
                      programIds.forEach((id) => {
                        const sanitizedProgramId = id.replace(/[^a-zA-Z0-9]/g, '');
                        if (sanitizedProgramId) {
                          params.append('programId', sanitizedProgramId);
                        }
                      });
                    }
                    const queryString = params.toString();
                    window.location.href = `${apiUrl}/auth/google${queryString ? `?${queryString}` : ''}`;
                  }}
                  aria-label="Sign in with Google"
                  className={`w-full flex items-center justify-center px-4 py-2.5 ${isPWA ? 'border-2 border-teal-200/60 bg-white hover:bg-teal-50/50 rounded-xl sm:rounded-2xl shadow-md hover:shadow-lg' : 'border border-gray-300 rounded-lg shadow-sm bg-white hover:bg-gray-50'} text-sm font-medium text-gray-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2`}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Sign in with Google
                </button>

                {/* Footer */}
                <div className={`text-center ${isPWA ? 'pt-5 border-t-2 border-teal-200/50' : 'pt-4 border-t border-gray-200'}`}>
                  <p className={`${isPWA ? 'text-sm sm:text-base text-teal-700' : 'text-sm text-gray-600'}`}>
                    Don't have an account?{' '}
                    <Link 
                      to={`/signup${location.search}`} 
                      className={`${isPWA ? 'font-bold text-teal-600 hover:text-teal-700 underline' : 'font-medium text-teal-600 hover:text-teal-500'}`}
                    >
                      Create one now
                    </Link>
                  </p>
                  <Link to="/forgot-password" className={`mt-2 inline-block ${isPWA ? 'text-sm sm:text-base font-semibold text-teal-600 hover:text-teal-700' : 'text-sm text-teal-600 hover:text-teal-500'}`}>
                    Forgot password?
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default Login;